import { useState, useEffect, useCallback } from "react";
import { boardAPI, inviteAPI, voteAPI, disruptionAPI, multiplaiAPI, deptAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import {
  UserPlus, Users, Check, ChevronRight, ChevronLeft, Shuffle, Loader2,
  Megaphone, TrendingUp, Headphones, DollarSign, Settings, Monitor,
  Lightbulb, Shield, Compass, ClipboardPaste, X, Send, Pencil, Save,
  Plus, Trash2, Play, Mail, Copy, RefreshCw, LayoutGrid, Link2, Unlink2,
} from "lucide-react";

const DEPT_ICONS = {
  megaphone: Megaphone, "trending-up": TrendingUp, headphones: Headphones,
  "dollar-sign": DollarSign, users: Users, settings: Settings,
  monitor: Monitor, lightbulb: Lightbulb, shield: Shield, compass: Compass,
};

const AVATAR_COLORS = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-cyan-500", "bg-violet-500", "bg-orange-500", "bg-teal-500"];
const getAC = (n) => AVATAR_COLORS[Math.abs([...(n || "")].reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0)) % AVATAR_COLORS.length];

const STEPS = [
  { label: "Invite Players", icon: UserPlus },
  { label: "Confirm Departments", icon: LayoutGrid },
  { label: "Assign Departments", icon: Settings },
  { label: "Assign Teams", icon: Users },
];

const TEAM_COLORS = [
  { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", dot: "bg-indigo-500" },
  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
  { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", dot: "bg-cyan-500" },
  { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500" },
];

export const SetupWizard = ({ board, onComplete, onRefresh }) => {
  const [step, setStep] = useState(board?.setup_step || 0);
  const [invitees, setInvitees] = useState([{ email: "", name: "" }]);
  const [bulkText, setBulkText] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [teams, setTeams] = useState([]);
  const [unassignedTeam, setUnassignedTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const [customMessage, setCustomMessage] = useState(
    `You've been invited to participate in the Unstoppable Company Game. Together we'll map our business workflows and make strategic AI decisions.\n\nPlease click the link below to join the session.`
  );
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [activeDepts, setActiveDepts] = useState([]);
  const [newDeptName, setNewDeptName] = useState("");
  const [multiplaiConnected, setMultiplaiConnected] = useState(false);
  const [multiplaiDepts, setMultiplaiDepts] = useState([]);
  const [multiplaiUsers, setMultiplaiUsers] = useState([]);
  const [deptSource, setDeptSource] = useState("default"); // "default" or "multiplai"
  const [loadingMultiplai, setLoadingMultiplai] = useState(false);

  const departments = board?.departments?.filter((d) => d.is_applicable) || [];
  const FRONTEND_URL = window.location.origin;

  const copyInviteLink = (token) => {
    navigator.clipboard.writeText(`${FRONTEND_URL}/invite/${token}`);
    setCopiedToken(token);
    toast.success("Link copied!");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const fetchParticipants = useCallback(async () => {
    try {
      const res = await inviteAPI.getParticipants(board.id);
      const pList = res.data.participants || [];
      setParticipants(pList);
      // Init assignments
      const map = {};
      pList.forEach((p) => { map[p.user_id] = new Set(p.assigned_departments || []); });
      setAssignments(map);
      // Init teams
      setUnassignedTeam(pList.map((p) => ({ user_id: p.user_id, name: p.name })));
    } catch { /* silent */ }
  }, [board?.id]);

  useEffect(() => { fetchParticipants(); }, [fetchParticipants]);

  // Init active departments from board
  useEffect(() => {
    if (board?.departments) {
      setActiveDepts(board.departments.map((d) => ({ ...d })));
    }
  }, [board?.departments]);

  // Check multiplai connection
  useEffect(() => {
    const checkMultiplai = async () => {
      try {
        const res = await multiplaiAPI.getStatus();
        setMultiplaiConnected(res.data.connected);
        if (res.data.connected) {
          const [dRes, uRes] = await Promise.all([multiplaiAPI.getDepartments(), multiplaiAPI.getUsers()]);
          setMultiplaiDepts(dRes.data.departments || []);
          setMultiplaiUsers(uRes.data.users || []);
        }
      } catch { setMultiplaiConnected(false); }
    };
    checkMultiplai();
  }, []);

  // --- Department Management ---
  const toggleDeptActive = (deptId) => {
    setActiveDepts((prev) => prev.map((d) => d.id === deptId ? { ...d, is_applicable: !d.is_applicable } : d));
  };

  const addCustomDept = async () => {
    if (!newDeptName.trim()) return;
    try {
      const res = await boardAPI.addDepartment(board.id, { name: newDeptName, icon: "settings", stalls: [] });
      setActiveDepts((prev) => [...prev, res.data]);
      setNewDeptName("");
      toast.success(`"${res.data.name}" added!`);
    } catch { toast.error("Failed to add department"); }
  };

  const importMultiplaiDept = async (mpDept) => {
    try {
      const res = await boardAPI.addDepartment(board.id, {
        name: mpDept.name,
        icon: mpDept.icon || "settings",
        stalls: mpDept.stalls || [],
      });
      setActiveDepts((prev) => [...prev, res.data]);
      toast.success(`Imported "${mpDept.name}" from multiplai.tech`);
    } catch { toast.error("Failed to import"); }
  };

  const saveDeptConfig = async () => {
    // Save department toggles
    for (const dept of activeDepts) {
      try {
        await deptAPI.toggle(board.id, dept.id, dept.is_applicable);
      } catch { /* silent */ }
    }
    toast.success("Department configuration saved!");
  };

  // --- Step 0: Invite ---
  const parseBulk = (text) => {
    return text.split(/[,;\n\t]+/).map((s) => s.trim()).filter(Boolean).map((entry) => {
      const m = entry.match(/^(.+?)\s*<([^>]+)>$/);
      if (m) return { name: m[1].trim(), email: m[2].trim().toLowerCase() };
      if (entry.includes("@")) return { name: "", email: entry.toLowerCase() };
      return null;
    }).filter(Boolean);
  };

  const applyBulk = () => {
    const parsed = parseBulk(bulkText);
    if (!parsed.length) { toast.error("No valid emails"); return; }
    const existing = new Set(invitees.map((i) => i.email).filter(Boolean));
    const newOnes = parsed.filter((p) => !existing.has(p.email));
    setInvitees((prev) => [...prev.filter((i) => i.email.trim()), ...newOnes]);
    setBulkText(""); setBulkMode(false);
    toast.success(`${newOnes.length} added`);
  };

  const sendInvites = async () => {
    const valid = invitees.filter((i) => i.email.trim());
    if (!valid.length) { toast.error("Add emails first"); return; }
    setSending(true);
    try {
      const res = await inviteAPI.send(board.id, { invitees: valid, custom_message: "" });
      toast.success(`${res.data.total_invited} invited!`);
      setInvitees([{ email: "", name: "" }]);
      fetchParticipants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  // --- Step 1: Assign Departments ---
  const toggleAssign = (userId, deptId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      const s = new Set(next[userId] || []);
      s.has(deptId) ? s.delete(deptId) : s.add(deptId);
      next[userId] = s;
      return next;
    });
  };

  const randomAssignDepts = () => {
    const deptIds = departments.map((d) => d.id);
    const pIds = participants.filter((p) => p.role !== "owner").map((p) => p.user_id);
    const map = {};
    // Owner gets all
    participants.filter((p) => p.role === "owner").forEach((p) => { map[p.user_id] = new Set(deptIds); });
    // Distribute departments round-robin to participants
    pIds.forEach((uid, idx) => {
      map[uid] = new Set();
      for (let d = 0; d < deptIds.length; d++) {
        if (d % Math.max(pIds.length, 1) === idx % Math.max(pIds.length, 1)) {
          map[uid].add(deptIds[d]);
        }
      }
    });
    setAssignments(map);
    toast.success("Randomly assigned!");
  };

  const assignAllDepts = () => {
    const deptIds = departments.map((d) => d.id);
    const map = {};
    participants.forEach((p) => { map[p.user_id] = new Set(deptIds); });
    setAssignments(map);
    toast.success("All departments assigned to everyone");
  };

  const saveDeptAssignments = async () => {
    setSaving(true);
    try {
      await Promise.all(Object.entries(assignments).map(([uid, deptSet]) =>
        voteAPI.assignDepartments(board.id, { user_id: uid, department_ids: [...deptSet] })
      ));
      toast.success("Department assignments saved!");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  // --- Step 2: Assign Teams ---
  const addTeam = () => {
    setTeams([...teams, { id: `t-${Date.now()}`, name: `Team ${teams.length + 1}`, members: [] }]);
  };

  const randomAssignTeams = () => {
    const all = [...unassignedTeam, ...teams.flatMap((t) => t.members)];
    const shuffled = [...all].sort(() => Math.random() - 0.5);
    const count = Math.max(teams.length, Math.ceil(shuffled.length / 4));
    const newTeams = Array.from({ length: count }, (_, i) => ({
      id: teams[i]?.id || `t-${Date.now()}-${i}`,
      name: teams[i]?.name || `Team ${i + 1}`,
      members: [],
    }));
    shuffled.forEach((p, i) => newTeams[i % count].members.push(p));
    setTeams(newTeams);
    setUnassignedTeam([]);
    toast.success("Randomly assigned to teams!");
  };

  const moveToTeam = (userId, fromTeamIdx, toTeamIdx) => {
    let user;
    if (fromTeamIdx === -1) {
      user = unassignedTeam.find((u) => u.user_id === userId);
      setUnassignedTeam((prev) => prev.filter((u) => u.user_id !== userId));
    } else {
      user = teams[fromTeamIdx]?.members.find((m) => m.user_id === userId);
      setTeams((prev) => prev.map((t, i) => i === fromTeamIdx ? { ...t, members: t.members.filter((m) => m.user_id !== userId) } : t));
    }
    if (!user) return;
    if (toTeamIdx === -1) {
      setUnassignedTeam((prev) => [...prev, user]);
    } else {
      setTeams((prev) => prev.map((t, i) => i === toTeamIdx ? { ...t, members: [...t.members, user] } : t));
    }
  };

  const saveTeams = async () => {
    setSaving(true);
    try {
      // Create disruption session if needed, then save groups
      try { await disruptionAPI.createSession(board.id); } catch { /* may already exist */ }
      await disruptionAPI.updateGroups(board.id, teams);
      toast.success("Teams saved for Part 2!");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  // --- Navigation ---
  const goNext = async () => {
    if (step === 0) { /* Invite step — just continue */ }
    if (step === 1) { await saveDeptConfig(); }
    if (step === 2) { await saveDeptAssignments(); }
    if (step === 3) { await saveTeams(); }

    const nextStep = step + 1;
    if (nextStep >= 4) {
      try { await boardAPI.updateSetupStep(board.id, 4); } catch {}
      onComplete?.();
      return;
    }
    try { await boardAPI.updateSetupStep(board.id, nextStep); } catch {}
    setStep(nextStep);
  };

  const StepIcon = STEPS[step]?.icon || Users;

  return (
    <div className="min-h-[60vh] max-w-4xl mx-auto px-6 py-8" data-testid="setup-wizard">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {STEPS.map((s, i) => {
          const SIcon = s.icon;
          return (
            <div key={i} className="flex items-center gap-2">
              <button onClick={() => i < step && setStep(i)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                i === step ? "bg-indigo-600 text-white" : i < step ? "bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200" : "bg-slate-100 text-slate-400"
              }`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : <SIcon className="w-3.5 h-3.5" />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
            </div>
          );
        })}
      </div>

      {/* Step 0: Invite Players */}
      {step === 0 && (
        <div className="space-y-6 animate-fade-in">
          <div className="text-center mb-6">
            <UserPlus className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-2xl text-slate-900">Invite Players</h2>
            <p className="text-sm text-slate-500 mt-1">Add your team members to the game board</p>
          </div>

          {/* Current participants */}
          {participants.length > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block">{participants.length} Participants</span>
              <div className="space-y-2">
                {participants.map((p, pi) => (
                  <div key={p.user_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${getAC(p.name)}`}>{(p.name || "?")[0].toUpperCase()}</div>
                      <span className="text-xs font-medium text-slate-700">{p.name}</span>
                      <span className="text-[10px] text-slate-400">{p.email}</span>
                      {p.role === "owner" && <Badge className="text-[8px] bg-indigo-50 text-indigo-600 border-indigo-200">Host</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {p.invite_token && p.role !== "owner" && (
                        <Button variant="outline" size="sm" onClick={() => copyInviteLink(p.invite_token)} className="h-6 text-[10px]">
                          {copiedToken === p.invite_token ? <><Check className="w-2.5 h-2.5 mr-0.5 text-emerald-500" />Copied</> : <><Copy className="w-2.5 h-2.5 mr-0.5" />Copy Link</>}
                        </Button>
                      )}
                      {p.role !== "owner" && p.status !== "active" && (
                        <Button variant="outline" size="sm" onClick={async () => {
                          try { await inviteAPI.send(board.id, { invitees: [{ email: p.email, name: p.name }], custom_message: customMessage }); toast.success(`Resent to ${p.email}`); } catch { toast.error("Failed"); }
                        }} className="h-6 text-[10px] text-indigo-600 border-indigo-200">
                          <RefreshCw className="w-2.5 h-2.5 mr-0.5" />Resend
                        </Button>
                      )}
                      <Badge className={`text-[8px] ${p.status === "active" || p.role === "owner" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
                        {p.role === "owner" ? "Host" : p.status === "active" ? "Joined" : "Pending"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Multiplai.tech Users */}
          {multiplaiConnected && multiplaiUsers.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-800">Select from multiplai.tech</span>
                <Badge className="text-[8px] bg-emerald-100 text-emerald-700 border-emerald-300">MOCK</Badge>
              </div>
              <div className="grid grid-cols-2 gap-1.5 max-h-[200px] overflow-y-auto">
                {multiplaiUsers.map((mu) => {
                  const alreadyInvited = participants.some((p) => p.email === mu.email) || invitees.some((i) => i.email === mu.email);
                  return (
                    <button key={mu.id} disabled={alreadyInvited}
                      onClick={() => {
                        if (!alreadyInvited) {
                          const clean = invitees.filter((i) => i.email.trim());
                          setInvitees([...clean, { name: mu.name, email: mu.email }]);
                          toast.success(`${mu.name} added to invite list`);
                        }
                      }}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs text-left transition-colors ${
                        alreadyInvited ? "border-emerald-200 bg-emerald-50/50 opacity-50" : "border-white bg-white hover:border-indigo-300"
                      }`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${getAC(mu.name)}`}>{(mu.name || "?")[0].toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-700 block truncate">{mu.name}</span>
                        <span className="text-[9px] text-slate-400">{mu.role} · {mu.department}</span>
                      </div>
                      {alreadyInvited ? <Check className="w-3 h-3 text-emerald-500 shrink-0" /> : <Plus className="w-3 h-3 text-slate-400 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Add Participants</span>
              <Button variant="ghost" size="sm" onClick={() => setBulkMode(!bulkMode)} className="h-7 text-xs text-indigo-600">
                <ClipboardPaste className="w-3 h-3 mr-1" />{bulkMode ? "Individual" : "Bulk Paste"}
              </Button>
            </div>
            {bulkMode ? (
              <div className="space-y-3">
                <Textarea placeholder="Paste emails: one per line, comma or semicolon separated" value={bulkText} onChange={(e) => setBulkText(e.target.value)} className="min-h-[100px] bg-slate-50 text-sm font-mono" />
                <Button onClick={applyBulk} disabled={!bulkText.trim()} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white"><Plus className="w-3 h-3 mr-1" />Add to List</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {invitees.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input placeholder="Name" value={row.name} onChange={(e) => setInvitees((p) => p.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))} className="h-9 bg-white flex-1" />
                    <Input placeholder="email@company.com" type="email" value={row.email} onChange={(e) => setInvitees((p) => p.map((r, i) => i === idx ? { ...r, email: e.target.value } : r))} className="h-9 bg-white flex-[2]" />
                    <Button variant="ghost" size="sm" onClick={() => invitees.length > 1 && setInvitees((p) => p.filter((_, i) => i !== idx))} disabled={invitees.length === 1} className="h-9 w-9 p-0 text-slate-400"><X className="w-3 h-3" /></Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setInvitees([...invitees, { email: "", name: "" }])} className="text-xs text-indigo-600"><Plus className="w-3 h-3 mr-1" />Add Another</Button>
              </div>
            )}
            <Button onClick={sendInvites} disabled={sending} className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white h-10">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Invites ({invitees.filter((i) => i.email.trim()).length})
            </Button>
          </div>

          {/* Email Preview */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <button onClick={() => setShowEmailPreview(!showEmailPreview)} className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Email Preview</span>
              </div>
              <span className="text-xs text-indigo-600">{showEmailPreview ? "Hide" : "Edit message"}</span>
            </button>
            {showEmailPreview && (
              <div className="border-t border-slate-100 animate-fade-in">
                <div className="bg-slate-900 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-indigo-500 rounded-md flex items-center justify-center">
                      <span className="text-white font-heading font-bold text-[10px]">U</span>
                    </div>
                    <span className="text-white font-heading font-semibold text-xs">Unstoppable Company Game</span>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Mail className="w-3 h-3" />
                    <span>To: participant@company.com</span>
                  </div>
                  <h3 className="font-heading font-bold text-base text-slate-900">You're Invited to the Unstoppable Company Game</h3>
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    className="min-h-[100px] text-sm border-slate-200 bg-slate-50"
                    placeholder="Custom message for invite email..."
                  />
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 space-y-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">Session Details</div>
                    <div className="text-sm text-slate-700 font-medium">{board?.name}</div>
                    <div className="text-xs text-slate-500">{board?.game_mode === "live" ? "Live Session" : "Async Game"}</div>
                  </div>
                  <div className="bg-indigo-600 text-white text-center py-2 rounded-lg text-sm font-medium">Join the Game</div>
                  <p className="text-[10px] text-slate-400 text-center">Email sending is a placeholder — invite links generated for now.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 1: Confirm Departments */}
      {step === 1 && (
        <div className="space-y-6 animate-fade-in">
          <div className="text-center mb-4">
            <LayoutGrid className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-2xl text-slate-900">Confirm Departments</h2>
            <p className="text-sm text-slate-500 mt-1">Review and customize which departments will be used in the game</p>
          </div>

          {/* Multiplai.tech connection */}
          {multiplaiConnected && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-800">multiplai.tech Connected</span>
                  <Badge className="text-[8px] bg-emerald-100 text-emerald-700 border-emerald-300">MOCK</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setDeptSource(deptSource === "default" ? "multiplai" : "default")}
                    className={`h-7 text-[10px] ${deptSource === "multiplai" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : ""}`}>
                    {deptSource === "multiplai" ? "Using Company Depts" : "Use Company Departments"}
                  </Button>
                </div>
              </div>
              {deptSource === "multiplai" && (
                <div className="mt-3 space-y-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Import from multiplai.tech</span>
                  <div className="grid grid-cols-2 gap-2">
                    {multiplaiDepts.map((mpd) => {
                      const alreadyAdded = activeDepts.some((d) => d.name === mpd.name);
                      return (
                        <button key={mpd.id} disabled={alreadyAdded} onClick={() => importMultiplaiDept(mpd)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs text-left transition-colors ${
                            alreadyAdded ? "border-emerald-200 bg-emerald-50 text-emerald-600 opacity-60" : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                          }`}>
                          {(() => { const I = DEPT_ICONS[mpd.icon] || Settings; return <I className="w-4 h-4 shrink-0" />; })()}
                          <div className="flex-1 min-w-0">
                            <span className="font-medium block truncate">{mpd.name}</span>
                            <span className="text-[9px] text-slate-400">{mpd.stalls?.length || 0} workflows</span>
                          </div>
                          {alreadyAdded ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Plus className="w-3.5 h-3.5 text-slate-400" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Current departments */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block">Active Departments ({activeDepts.filter((d) => d.is_applicable).length})</span>
            <div>
              <div className="space-y-1.5">
                {activeDepts.map((dept) => {
                  const Icon = DEPT_ICONS[dept.icon] || Settings;
                  return (
                    <div key={dept.id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${dept.is_applicable ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-50"}`}>
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-slate-500 shrink-0" />
                        <div>
                          <span className="text-sm font-medium text-slate-800">{dept.name}</span>
                          <span className="text-[10px] text-slate-400 ml-2">{dept.stalls?.length || 0} workflows</span>
                          {dept.is_custom && <Badge className="ml-2 text-[8px] bg-indigo-50 text-indigo-600 border-indigo-200">Custom</Badge>}
                        </div>
                      </div>
                      <Switch checked={dept.is_applicable} onCheckedChange={() => toggleDeptActive(dept.id)} className="scale-90" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Add custom department */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 block">Add Custom Department</span>
            <div className="flex items-center gap-2">
              <Input placeholder="Department name (e.g., Partnerships)" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustomDept()} className="h-9 bg-white flex-1 text-sm" />
              <Button onClick={addCustomDept} disabled={!newDeptName.trim()} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
                <Plus className="w-3.5 h-3.5 mr-1" />Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Assign Departments to Players */}
      {step === 2 && (
        <div className="space-y-6 animate-fade-in">
          <div className="text-center mb-4">
            <Settings className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-2xl text-slate-900">Assign Departments</h2>
            <p className="text-sm text-slate-500 mt-1">Assign players to departments for Part 1. Players can be assigned to multiple departments.</p>
          </div>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={randomAssignDepts} className="h-8 text-xs"><Shuffle className="w-3 h-3 mr-1" />Random</Button>
            <Button variant="outline" size="sm" onClick={assignAllDepts} className="h-8 text-xs"><Users className="w-3 h-3 mr-1" />Assign All</Button>
          </div>
          <div>
            <div className="space-y-3">
              {participants.map((p) => {
                const userDepts = assignments[p.user_id] || new Set();
                return (
                  <div key={p.user_id} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${getAC(p.name)}`}>{(p.name || "?")[0].toUpperCase()}</div>
                      <span className="text-sm font-medium text-slate-800">{p.name}</span>
                      <Badge variant="outline" className="text-[9px] font-mono">{userDepts.size}/{departments.length}</Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5">
                      {departments.map((dept) => {
                        const Icon = DEPT_ICONS[dept.icon] || Settings;
                        const checked = userDepts.has(dept.id);
                        return (
                          <button key={dept.id} onClick={() => toggleAssign(p.user_id, dept.id)}
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[10px] transition-colors ${
                              checked ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                            }`}>
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-indigo-500 border-indigo-500" : "border-slate-300"}`}>
                              {checked && <Check className="w-2 h-2 text-white" />}
                            </div>
                            <span className="truncate">{dept.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Assign Teams */}
      {step === 3 && (
        <div className="space-y-6 animate-fade-in">
          <div className="text-center mb-4">
            <Users className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <h2 className="font-heading font-bold text-2xl text-slate-900">Assign Teams for Part 2</h2>
            <p className="text-sm text-slate-500 mt-1">Create brainstorm teams for the Radical Disruption session</p>
          </div>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={addTeam} className="h-8 text-xs"><Plus className="w-3 h-3 mr-1" />Add Team</Button>
            <Button variant="outline" size="sm" onClick={randomAssignTeams} className="h-8 text-xs"><Shuffle className="w-3 h-3 mr-1" />Random Assign</Button>
            <Badge variant="outline" className="text-[10px]">{teams.length} teams</Badge>
          </div>

          {/* Unassigned */}
          {unassignedTeam.length > 0 && (
            <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 block">Unassigned ({unassignedTeam.length})</span>
              <div className="flex flex-wrap gap-1.5">
                {unassignedTeam.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-xs">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white ${getAC(u.name)}`}>{(u.name || "?")[0].toUpperCase()}</div>
                    <span>{u.name}</span>
                    {teams.length > 0 && (
                      <select onChange={(e) => { if (e.target.value) moveToTeam(u.user_id, -1, parseInt(e.target.value)); e.target.value = ""; }} className="text-[9px] bg-transparent border-none text-indigo-600 cursor-pointer p-0 w-4" defaultValue="">
                        <option value="" disabled>+</option>
                        {teams.map((t, ti) => <option key={ti} value={ti}>{t.name}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Teams */}
          <div className="space-y-3">
            {teams.map((team, tIdx) => {
              const tc = TEAM_COLORS[tIdx % TEAM_COLORS.length];
              return (
                <div key={team.id} className={`${tc.bg} rounded-xl border ${tc.border} p-4`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${tc.dot}`} />
                      <Input value={team.name} onChange={(e) => setTeams((p) => p.map((t, i) => i === tIdx ? { ...t, name: e.target.value } : t))} className="h-7 w-32 text-xs bg-white border-none font-heading font-semibold" />
                      <Badge variant="outline" className="text-[9px]">{team.members.length}</Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setUnassignedTeam((p) => [...p, ...team.members]); setTeams((p) => p.filter((_, i) => i !== tIdx)); }} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 min-h-[32px]">
                    {team.members.map((m) => (
                      <div key={m.user_id} className="flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-xs">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white ${getAC(m.name)}`}>{(m.name || "?")[0].toUpperCase()}</div>
                        <span className="text-slate-700">{m.name}</span>
                        <button onClick={() => moveToTeam(m.user_id, tIdx, -1)} className="text-slate-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {team.members.length === 0 && <span className="text-[10px] text-slate-400 italic py-1">Assign players from unassigned pool</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {teams.length === 0 && (
            <div className="text-center py-6 text-slate-400">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Add teams or use Random Assign</p>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} className="h-10">
          <ChevronLeft className="w-4 h-4 mr-1.5" />Back
        </Button>
        <div className="flex items-center gap-3">
          {step < 3 && (
            <Button variant="ghost" onClick={() => { goNext(); }} className="text-sm text-slate-400">
              Skip this step
            </Button>
          )}
          <Button onClick={goNext} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white h-10 px-6 font-medium">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {step === 3 ? (
              <><Play className="w-4 h-4 mr-1.5" />Start Game</>
            ) : (
              <><ChevronRight className="w-4 h-4 mr-1.5" />Next Step</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
